import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { dateFnsLocaleMap, getDateFormat } from '../../utils/date.js';
import { noop } from '../../utils/util.js';
import { DatePicker } from '../DatePicker/DatePicker.js';
import { DateInputBase } from './DateInputBase.js';
import { DateInputPlaceholder } from './DateInputPlaceholder.js';
import { DateSpinButton } from './DateSpinButton.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["languageCode", "locale", "onBlur", "onChange", "placeholder", "time", "value"];

function getDateTimeFormat(_ref) {
  var _locale$formatLong, _locale$formatLong2;

  var locale = _ref.locale,
      time = _ref.time;
  var f;
  var dateFormat = getDateFormat(locale);
  var timeFormat = locale === null || locale === void 0 ? void 0 : (_locale$formatLong = locale.formatLong) === null || _locale$formatLong === void 0 ? void 0 : _locale$formatLong.time({
    width: "medium"
  });
  var dateTimeFormat = locale === null || locale === void 0 ? void 0 : (_locale$formatLong2 = locale.formatLong) === null || _locale$formatLong2 === void 0 ? void 0 : _locale$formatLong2.dateTime({
    width: "medium"
  });

  if (time) {
    f = dateTimeFormat.replace("{{date}}", dateFormat);
    f = f.replace("{{time}}", timeFormat);

    if (time.clock === "24hour" && timeFormat.match(/h+/)) {
      f = f.replace(/h/g, "H");
      f = f.replace(/a+/, "").trim();
    }
  } else {
    f = dateFormat;
  }

  return f;
}

var DateInput = /*#__PURE__*/React.forwardRef(function DateInput2(props, ref) {
  var languageCode = props.languageCode,
      locale = props.locale,
      onBlur = props.onBlur,
      _props$onChange = props.onChange,
      onChange = _props$onChange === void 0 ? noop : _props$onChange,
      placeholder = props.placeholder,
      time = props.time,
      value = props.value,
      dateFieldBaseProps = _objectWithoutProperties(props, _excluded);

  var _React$useState = React.useState(!value),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      placeholderVisible = _React$useState2[0],
      setPlaceholderVisible = _React$useState2[1];

  var _useThemeContext = useThemeContext(),
      localization = _useThemeContext.localization;

  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
      t = _useTranslations.t;

  var themeProvidedLocale = (localization === null || localization === void 0 ? void 0 : localization.languageCode) && dateFnsLocaleMap[localization === null || localization === void 0 ? void 0 : localization.languageCode] || dateFnsLocaleMap["en-US"];
  var componentProvidedLocale = languageCode && dateFnsLocaleMap[languageCode] || locale;
  var currentLocale = componentProvidedLocale || themeProvidedLocale;
  var dateFormat = getDateTimeFormat({
    time: time,
    locale: currentLocale
  });
  return /*#__PURE__*/jsx(DateInputBase, _objectSpread(_objectSpread({}, dateFieldBaseProps), {}, {
    tabIndex: -1,
    onBlur: onBlur,
    closeButtonProps: {
      onClick: function onClick(e) {
        e.stopPropagation();
        onChange(null);
        setPlaceholderVisible(true);
      }
    },
    locale: currentLocale,
    ref: ref,
    renderDatePicker: function renderDatePicker(datePickerProps) {
      return /*#__PURE__*/jsx(DatePicker, _objectSpread(_objectSpread({}, datePickerProps), {}, {
        variant: {
          onChange: onChange,
          time: time,
          type: "date",
          value: value
        }
      }));
    },
    renderSpinButton: function renderSpinButton(spinButtonProps) {
      return /*#__PURE__*/jsxs("div", {
        style: {
          position: "relative",
          whiteSpace: "nowrap"
        },
        onFocus: function onFocus() {
          return setPlaceholderVisible(!!value);
        },
        children: [placeholderVisible && !value && /*#__PURE__*/jsx(DateInputPlaceholder, {
          placeholder: placeholder
        }), /*#__PURE__*/jsx(DateSpinButton, _objectSpread(_objectSpread({}, spinButtonProps), {}, {
          "aria-label": t("DateInput.spinButtonLabel"),
          dateFormat: dateFormat,
          locale: currentLocale,
          onBlur: onBlur,
          onChange: onChange,
          value: value
        }))]
      });
    }
  }));
});
DateInput.propTypes = {
  "languageCode": _PropTypes.oneOf(["de-DE", "en-GB", "en-US", "es-ES", "fr-CA", "fr-FR", "id-ID", "it-IT", "ja-JP", "ko-KR", "pt-BR", "ru-RU", "tr-TR", "zh-CN", "zh-TW"]),
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  }),
  "onBlur": _PropTypes.func,
  "onChange": _PropTypes.func,
  "placeholder": _PropTypes.string,
  "time": _PropTypes.shape({
    "clock": _PropTypes.oneOf(["12hour", "24hour"]),
    "timezone": _PropTypes.shape({
      "error": _PropTypes.bool,
      "onChange": _PropTypes.func,
      "options": _PropTypes.arrayOf(_PropTypes.object).isRequired,
      "value": _PropTypes.instanceOf(Date)
    })
  }),
  "value": _PropTypes.instanceOf(Date)
};
DateInput.displayName = "DateInput";

export { DateInput };
